import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { ProjectTimeChartPointsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { overviewFrequencyOptionsEnum } from 'modules/project_management/lib/constants'
import { getDecimalHoursFromMinutes } from 'modules/project_management/lib/formatHours'
import { OverviewFilterOption } from 'modules/project_management/lib/overviewOptions'

import { ChartInsight } from '../../ChartInsight'
import { ResponsiveLineChart } from '../../ResponsiveLineChart'
import {
  ConstantTransformedData,
  EvolutionTransformedData,
  getEvolutionTransformedData,
} from '../getEvolutionTransformedData'

interface EvolutionGraphProps {
  projectId: number
  startDate?: moment.Moment
  endDate?: moment.Moment
  selectedFrequency: overviewFrequencyOptionsEnum
  selectedFilter: OverviewFilterOption
  fixedTimeBudget?: number
  hasBudgetFeature: boolean
}

export const TimeEvolutionChart = ({
  projectId,
  startDate = moment().startOf('week'),
  endDate = moment().endOf('week'),
  selectedFrequency,
  selectedFilter,
  fixedTimeBudget,
  hasBudgetFeature,
}: EvolutionGraphProps) => {
  const [evolutionDataShown, setEvolutionDataShown] = useState<boolean>(true)

  const { loading, error, data } = useQuery(ProjectTimeChartPointsDocument, {
    variables: {
      projectId,
      endDate: endDate.format('YYYY-MM-DD'),
      startDate: startDate.format('YYYY-MM-DD'),
      slotType: selectedFrequency,
    },
  })

  const timeChartPoints = data ? data.projectManagement.projectTimeGraphs : []

  const registeredHoursData: EvolutionTransformedData = getEvolutionTransformedData(
    timeChartPoints,
    selectedFrequency
  )

  const timeBudgetLimit: ConstantTransformedData | undefined = hasBudgetFeature
    ? {
        id: 'time_budget',
        label: i18n.t('project_management.graphs.evolution.time.legends.time_budget'),
        value: (fixedTimeBudget && getDecimalHoursFromMinutes(fixedTimeBudget)) || 0,
        color: 'grey800',
      }
    : undefined

  const onChangeEvolutionDataShown = (isShown: boolean) => {
    setEvolutionDataShown(isShown)
  }

  return (
    <Box gap="s12">
      <ChartInsight
        id={'registered_hours'}
        title={selectedFilter.label}
        startingValue={evolutionDataShown ? registeredHoursData.min : 0}
        endingValue={evolutionDataShown ? registeredHoursData.max : 0}
        color={evolutionDataShown ? registeredHoursData.color : 'grey400'}
      />
      <Text.Normal color={'grey800'} weight="medium" size="300">
        {selectedFilter.subtitle}
      </Text.Normal>
      <ResponsiveLineChart
        evolutionData={registeredHoursData}
        constantData={timeBudgetLimit?.value ? [timeBudgetLimit] : []}
        unit={i18n.t('project_management.graphs.evolution.time.unit')}
        onChangeEvolutionDataShown={onChangeEvolutionDataShown}
        selectedFrequency={selectedFrequency}
        loading={loading}
        error={error}
      />
    </Box>
  )
}
